import { useCallback, useEffect, useState } from 'react'
import type { FieldErrors, UseFormRegister } from 'react-hook-form'
import DktLoader from 'src/components/DktLoader/DktLoader'
import type { AddressSchemaType } from 'src/components/checkout/steps/shipping/components/Address/AddressForm'

import { PostalInput } from './PostalInput'
import type { ReferenceChipId } from './ReferenceNicknamePicker'
import {
  CHIP_LABEL_BY_ID,
  ReferenceNicknamePicker,
} from './ReferenceNicknamePicker'

interface FormFieldsProps {
  formErrors: FieldErrors<AddressSchemaType>
  register: UseFormRegister<AddressSchemaType>
  isLoading: boolean
  disabledFields?: Record<string, boolean>
  onCancel: () => void
  submitButtonLabel?: string
  initialReference?: string
  syncFormReference?: (value: string) => void
}

const FIELD_ROW_LAYOUT = 'flex flex-col gap-4 md:flex-row md:gap-4'

const postalInputBasics = {
  classContainer: 'w-full sm:w-full',
  wrapperClassName: 'w-full',
  labelClassname: 'text-body-sm text-restructure-primary pb-2',
  className:
    'checkout-profile-input w-full border-neutral01 border text-body-md text-restructure-primary p-[13.75px] h-11 rounded-none hover:rounded-none hover:outline-none hover:border-2 hover:border-primaryBlue focus:rounded-none focus:outline-none focus:border-2 focus:border-primaryBlue',
  classNameError: 'border-redDiscount',
} as const

const FIELD_LABEL_SEMIBOLD =
  'text-font-size-2 text-restructure-primary font-semibold mb-2'

const GATEWAY_FIELD_INPUT_CLASS =
  'border checkout-profile-input w-full border-neutral01 text-font-size-3 text-restructure-primary p-[13.75px] h-11 rounded-none hover:rounded-none hover:outline-none hover:border-2 hover:border-primaryBlue focus:rounded-none focus:outline-none focus:border-2 focus:border-primaryBlue disabled:bg-postal-input-disabled disabled:text-[#000] disabled:hover:border-neutral01'

const NORMALIZED_REFERENCE_PRESET_LOOKUP: Record<string, ReferenceChipId> = {
  casa: 'home',
  trabalho: 'work',
  outro: 'other',
  home: 'home',
  house: 'home',
  work: 'work',
  office: 'work',
  other: 'other',
}

interface ReferencePresetSplit {
  option: ReferenceChipId
  other: string
}

function splitInitialReference(value?: string): ReferencePresetSplit {
  const trimmed = typeof value === 'string' ? value.trim() : ''

  if (!trimmed) {
    return { option: 'home', other: '' }
  }

  const preset = NORMALIZED_REFERENCE_PRESET_LOOKUP[trimmed.toLowerCase()]

  if (preset) {
    return { option: preset, other: '' }
  }

  return { option: 'other', other: trimmed }
}

export const PostalFormFields = ({
  formErrors,
  register,
  isLoading,
  disabledFields = {},
  onCancel,
  submitButtonLabel = 'Salvar e usar este endereço',
  initialReference = '',
  syncFormReference,
}: FormFieldsProps) => {
  const [
    { option: referenceOption, other: referenceOther },
    setReferencePreset,
  ] = useState<ReferencePresetSplit>(() =>
    splitInitialReference(initialReference)
  )

  const referenceValue =
    referenceOption === 'other'
      ? referenceOther
      : CHIP_LABEL_BY_ID[referenceOption]

  const referenceFieldReg = register('reference')

  useEffect(() => {
    syncFormReference?.(referenceValue)
  }, [referenceValue, syncFormReference])

  useEffect(() => {
    setReferencePreset(splitInitialReference(initialReference))
  }, [initialReference])

  const registerForPostalInputs = register as UseFormRegister<{
    reference?: string
    street?: string
    city?: string
    state?: string
    neighborhood?: string
    number?: string
    complement?: string
    receiverName?: string
  }>

  const handleReferenceChipSelection = useCallback(
    (option: ReferenceChipId) => {
      setReferencePreset((prev) => ({
        option,
        other: option !== 'other' ? '' : prev.other,
      }))
    },
    []
  )

  const handleReferenceControlledInput = useCallback(
    (rawValue: string, presetLabelForRhf: string) => {
      if (referenceOption === 'other') {
        setReferencePreset((prev) => ({ ...prev, other: rawValue }))
      }

      const nextValue =
        referenceOption === 'other' ? rawValue : presetLabelForRhf

      referenceFieldReg.onChange({ target: { value: nextValue } })
    },
    [referenceOption, referenceFieldReg]
  )

  return (
    <>
      <PostalInput
        {...postalInputBasics}
        name="street"
        label="Rua / Logradouro"
        errors={formErrors?.street}
        id="inputStreet"
        register={registerForPostalInputs}
        labelClassname={FIELD_LABEL_SEMIBOLD}
        disabled={!!disabledFields.street}
        required
        placeholder="Ex: Av. Brasil"
        className={GATEWAY_FIELD_INPUT_CLASS}
      />
      <div className={FIELD_ROW_LAYOUT}>
        <PostalInput
          {...postalInputBasics}
          name="number"
          label="Número"
          errors={formErrors?.number}
          id="inputNumber"
          register={registerForPostalInputs}
          disabled={!!disabledFields.number}
          labelClassname={FIELD_LABEL_SEMIBOLD}
          classContainer="md:w-1/3"
          placeholder="123"
          className={GATEWAY_FIELD_INPUT_CLASS}
          required
        />
        <PostalInput
          {...postalInputBasics}
          name="complement"
          label="Complemento"
          errors={formErrors?.complement}
          id="inputComplement"
          register={registerForPostalInputs}
          labelClassname={FIELD_LABEL_SEMIBOLD}
          disabled={!!disabledFields.complement}
          classContainer="md:w-2/3"
          placeholder="Apto, bloco, ponto de referência…"
          required={false}
        />
      </div>
      <div className={FIELD_ROW_LAYOUT}>
        <PostalInput
          {...postalInputBasics}
          name="neighborhood"
          label="Bairro"
          errors={formErrors?.neighborhood}
          id="inputNeighborhood"
          register={registerForPostalInputs}
          labelClassname={FIELD_LABEL_SEMIBOLD}
          classContainer="md:w-1/2"
          disabled={!!disabledFields.neighborhood}
          placeholder="Ex: Centro"
          className={GATEWAY_FIELD_INPUT_CLASS}
          required
        />
        <PostalInput
          {...postalInputBasics}
          name="city"
          label="Cidade"
          errors={formErrors?.city}
          id="inputCity"
          register={registerForPostalInputs}
          labelClassname={FIELD_LABEL_SEMIBOLD}
          disabled={!!disabledFields.city}
          placeholder="São Paulo"
          className={GATEWAY_FIELD_INPUT_CLASS}
          classContainer="md:w-1/3"
          required
        />
        <PostalInput
          {...postalInputBasics}
          name="state"
          label="UF"
          errors={formErrors?.state}
          id="inputState"
          register={registerForPostalInputs}
          labelClassname={FIELD_LABEL_SEMIBOLD}
          placeholder="SP"
          className={GATEWAY_FIELD_INPUT_CLASS}
          disabled={!!disabledFields.state}
          classContainer="md:w-1/6"
          required
        />
      </div>
      <ReferenceNicknamePicker
        referenceOption={referenceOption}
        referenceValue={referenceValue}
        inputClassName={GATEWAY_FIELD_INPUT_CLASS}
        referenceFieldReg={referenceFieldReg}
        onChipSelect={handleReferenceChipSelection}
        onReferenceInputChange={handleReferenceControlledInput}
      />
      <PostalInput
        {...postalInputBasics}
        name="receiverName"
        label="Destinatário"
        errors={formErrors?.receiverName}
        placeholder="Insira o nome do destinatário"
        labelClassname={FIELD_LABEL_SEMIBOLD}
        id="inputreceiverName"
        register={registerForPostalInputs}
        disabled={!!disabledFields.receiverName}
        required
      />
      <div className="flex gap-6 items-center justify-end">
        <button
          type="button"
          className="text-font-size-3 text-postal-muted font-semibold"
          onClick={onCancel}
        >
          Cancelar
        </button>
        <button
          type="submit"
          className="h-11 py-2 px-6 bg-postal-accent text-font-size-3 font-bold text-restructure-primary-inverted flex items-center justify-center gap-2"
          disabled={isLoading}
          aria-busy={isLoading}
        >
          {isLoading ? (
            <>
              <span>Aguarde...</span>
              <DktLoader width={14} variant="white" />
            </>
          ) : (
            <>{submitButtonLabel}</>
          )}
        </button>
      </div>
    </>
  )
}
